/**
 * Barras de vida dos inimigos (DOM, pooled, projetadas na tela). Aparecem quando o
 * inimigo está em combate, é o alvo atual, ou está ferido; somem quando cheio e fora
 * de combate. Elite tem estilo diferenciado. O Boss usa a BossHUD dedicada (não entra aqui).
 */
import * as THREE from "three";
import { i18n } from "../i18n/i18n";
import { weaponTypeSvg } from "../items/icons";
import { fmtNum } from "../utils/format";
import { quality } from "../core/Quality";
import type { Enemy } from "../enemies/Enemy";

interface Bar {
  wrap: HTMLDivElement;
  fill: HTMLDivElement;
  num: HTMLDivElement;
  name: HTMLDivElement;
  /** 10.6G — etiquetas "fraco a X / aguenta Y" (armas). */
  tags: HTMLDivElement;
  tagKey: string; // cache: só redesenha o HTML quando o par muda
}

const _v = new THREE.Vector3();

export class EnemyHealthBars {
  private readonly layer: HTMLDivElement;
  private readonly pool: Bar[] = [];

  constructor(
    uiRoot: HTMLElement,
    private readonly camera: THREE.PerspectiveCamera,
  ) {
    this.layer = document.createElement("div");
    this.layer.className = "ehb-layer";
    uiRoot.appendChild(this.layer);
  }

  /** 10.9HZ — buffer reaproveitado pra ordenar por proximidade sem alocar a cada quadro. */
  private readonly nearBuf: Enemy[] = [];

  update(enemies: readonly Enemy[], target: Enemy | null): void {
    let used = 0;
    const w = window.innerWidth;
    const h = window.innerHeight;
    // 10.9HY — TETO DE BARRAS pelo preset (low = 8). Antes percorria TODOS os vivos (até 260),
    // fazendo um project() por inimigo e ~6 escritas de DOM por barra visível, todo quadro —
    // era um dos maiores custos de CPU/layout no celular fraco.
    // 10.9HZ — e o teto agora pega os MAIS PRÓXIMOS. Cortando pela ordem do array (ordem de
    // spawn) as 8 barras iam para os sobreviventes mais antigos — em geral os mais LONGE — e o
    // bando que estava te batendo ficava sem nenhuma; pior, a lista se reordena a cada morte
    // (splice no meio) e as barras pulavam de monstro.
    const maxBars = quality.values.enemyBars;
    const cam = this.camera.position;
    let list: readonly Enemy[] = enemies;
    if (enemies.length > maxBars) {
      const near = this.nearBuf;
      near.length = 0;
      for (const e of enemies) if (!e.isDead) near.push(e);
      near.sort((a, b) => {
        const da = (a.position.x - cam.x) ** 2 + (a.position.z - cam.z) ** 2;
        const db = (b.position.x - cam.x) ** 2 + (b.position.z - cam.z) ** 2;
        return da - db;
      });
      list = near;
    }
    for (const e of list) {
      if (e.isDead) continue;
      if (used >= maxBars && e !== target) continue; // o alvo atual sempre tem barra
      // 10.6F — a barra some quando o monstro está cheio e fora de combate, então metade
      // do campo ficava sem indicação nenhuma. Agora TODO monstro vivo na tela mostra vida.
      _v.set(e.position.x, e.barAnchorY, e.position.z);
      _v.project(this.camera);
      if (_v.z > 1) continue; // atrás da câmera
      if (_v.x < -1.1 || _v.x > 1.1 || _v.y < -1.1 || _v.y > 1.1) continue; // fora do enquadramento

      const bar = this.acquire(used++);
      const sx = (_v.x * 0.5 + 0.5) * w;
      const sy = (-_v.y * 0.5 + 0.5) * h;
      bar.wrap.style.transform = `translate(-50%,-50%) translate(${sx}px, ${sy}px)`;
      bar.wrap.classList.toggle("ehb-elite", e.elite);
      bar.wrap.classList.toggle("ehb-target", e === target);
      bar.fill.style.transform = `scaleX(${Math.max(0, e.healthFraction)})`;
      // regen visível: leve brilho quando curando (fora de combate, HP subindo)
      bar.fill.classList.toggle("ehb-regen", !e.inCombat && e.healthFraction < 0.999);
      bar.num.textContent = `${fmtNum(Math.ceil(e.hp))}/${fmtNum(e.maxHp)}`;
      const label = i18n.t(e.nameKey);
      bar.name.textContent = e.elite ? `${label} · ELITE` : label;
      // 10.6G — ETIQUETAS DE ARMA. É aqui que o jogador aprende o sistema: a barra do
      // monstro diz, em cima dele, qual arma o machuca e qual ele aguenta.
      // Três níveis, lidos só pela COR do ícone (sem texto, como num jogo de arena):
      // VERMELHO = ele segura essa arma · LARANJA = mediano · VERDE = essa arma o machuca.
      const key = `${e.weakTo ?? ""}|${e.tough ?? ""}|${e.resists ?? ""}`;
      if (bar.tagKey !== key) {
        bar.tagKey = key;
        let html = "";
        if (e.resists) html += `<span class="ehb-tag red" title="${i18n.t("combat.tough")}">${weaponTypeSvg(e.resists, "#fff1f1")}</span>`;
        if (e.tough) html += `<span class="ehb-tag amber">${weaponTypeSvg(e.tough, "#2b1a00")}</span>`;
        if (e.weakTo) html += `<span class="ehb-tag green" title="${i18n.t("combat.weakTo")}">${weaponTypeSvg(e.weakTo, "#07230f")}</span>`;
        bar.tags.innerHTML = html;
      }
    }
    // barras não usadas: fade-out suave (não some instantâneo)
    for (let i = used; i < this.pool.length; i++) this.pool[i].wrap.classList.add("ehb-off");
  }

  private acquire(i: number): Bar {
    let bar = this.pool[i];
    if (!bar) {
      const wrap = document.createElement("div");
      wrap.className = "ehb";
      wrap.innerHTML = `<div class="ehb-tags"></div><div class="ehb-name"></div><div class="ehb-bar"><div class="ehb-fill"></div></div><div class="ehb-num"></div>`;
      this.layer.appendChild(wrap);
      bar = {
        wrap,
        fill: wrap.querySelector(".ehb-fill")!,
        num: wrap.querySelector(".ehb-num")!,
        name: wrap.querySelector(".ehb-name")!,
        tags: wrap.querySelector(".ehb-tags")!,
        tagKey: "",
      };
      this.pool[i] = bar;
    }
    bar.wrap.hidden = false;
    bar.wrap.classList.remove("ehb-off");
    return bar;
  }

  clear(): void {
    for (const b of this.pool) b.wrap.classList.add("ehb-off");
  }
}
